/**
 * Sandboxes list page component.
 *
 * @module pages/SandboxesPage
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useParams } from 'react-router';
import {
  Box,
  Search,
  LayoutGrid,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Square,
  Play,
  FolderOpen,
  Layers,
  Github,
  Loader2,
  Terminal,
} from 'lucide-react';
import { LoadingPage, Badge, ConfirmModal, getSandboxStateBadgeVariant } from '@/components/ui';
import {
  useSandboxes,
  useSandboxesCount,
  useDeleteSandbox,
  useStopSandbox,
  useResumeSandbox,
  useSandboxGroups,
  useSandboxGroupSandboxes,
  sandboxGroupKeys,
} from '@/hooks';
import { sandboxService } from '@/services';
import { useQueryClient } from '@tanstack/react-query';
import type { SandboxResponse } from '@/types';

/**
 * Sandboxes list page
 */
export function SandboxesPage() {
  const { groupId } = useParams<{ groupId?: string }>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { data: allSandboxes, isLoading: allLoading } = useSandboxes(page, pageSize);
  const { data: allTotal = 0 } = useSandboxesCount();
  const { data: groupSandboxes, isLoading: groupLoading } = useSandboxGroupSandboxes(
    groupId ?? '',
    page,
    pageSize
  );
  // When in group scope, use the group-scoped data
  const sandboxes = groupId ? groupSandboxes : allSandboxes;
  const total = groupId ? (groupSandboxes?.length ?? 0) : allTotal;
  const isLoading = groupId ? groupLoading : allLoading;
  const { data: sandboxGroups } = useSandboxGroups(1, 100);
  const deleteMutation = useDeleteSandbox();
  const stopMutation = useStopSandbox();
  const resumeMutation = useResumeSandbox();
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<SandboxResponse | null>(null);
  const [stopConfirmTarget, setStopConfirmTarget] = useState<SandboxResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({
    current: 0,
    total: 0,
    currentId: '',
  });
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [groupByGroup, setGroupByGroup] = useState(false);
  const [showGroupByMenu, setShowGroupByMenu] = useState(false);
  const [showCreateSandboxMenu, setShowCreateSandboxMenu] = useState(false);
  const createSandboxMenuRef = useRef<HTMLDivElement>(null);

  // Track which sandboxes are currently resuming/stopping (allows concurrent operations)
  const [resumingIds, setResumingIds] = useState<Set<string>>(new Set());
  const [stoppingIds, setStoppingIds] = useState<Set<string>>(new Set());

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        createSandboxMenuRef.current &&
        !createSandboxMenuRef.current.contains(event.target as Node)
      ) {
        setShowCreateSandboxMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear stale selections when the sandbox list changes (e.g. after deletion)
  useEffect(() => {
    if (!sandboxes) return;
    const currentIds = new Set(sandboxes.map((s) => s.id));
    setSelectedIds((prev) => {
      const pruned = new Set([...prev].filter((id) => currentIds.has(id)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [sandboxes]);

  // Filter sandboxes based on search query
  const filteredSandboxes = sandboxes?.filter((sandbox) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();

    // Search in sandbox ID
    if (sandbox.id.toLowerCase().includes(query)) {
      return true;
    }

    // Search in labels (keys and values)
    if (sandbox.labels) {
      const labelsMatch = Object.entries(sandbox.labels as Record<string, string>).some(
        ([key, value]) => key.toLowerCase().includes(query) || value.toLowerCase().includes(query)
      );
      if (labelsMatch) {
        return true;
      }
    }

    return false;
  });

  // Group sandboxes by sandboxGroupId when grouping is enabled
  const groupedSandboxes = useMemo(() => {
    if (!groupByGroup || !filteredSandboxes) return null;

    const groups: Map<string | null, SandboxResponse[]> = new Map();

    for (const sandbox of filteredSandboxes) {
      const groupId = sandbox.sandboxGroupId || null;
      if (!groups.has(groupId)) {
        groups.set(groupId, []);
      }
      groups.get(groupId)!.push(sandbox);
    }

    // Sort: named groups first (alphabetically), then ungrouped at the end
    const sortedEntries = Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return a.localeCompare(b);
    });

    return sortedEntries;
  }, [groupByGroup, filteredSandboxes]);

  // Get group name by ID
  const getGroupName = (groupId: string | null): string => {
    if (!groupId) return 'Ungrouped';
    const group = sandboxGroups?.find((g) => g.id === groupId);
    if (group?.labels && typeof group.labels === 'object') {
      const name = (group.labels as Record<string, string>)['name'];
      if (name) return name;
    }
    return groupId;
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(
        {
          id: deleteTarget.id,
          sandboxGroupId: deleteTarget.sandboxGroupId ?? undefined,
        },
        {
          onSuccess: () => setDeleteTarget(null),
        }
      );
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleFirstPage = () => {
    setPage(1);
  };

  const handleLastPage = () => {
    setPage(totalPages);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && filteredSandboxes) {
      setSelectedIds(new Set(filteredSandboxes.map((s) => s.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setIsBulkDeleting(true);
    const idsToDelete = Array.from(selectedIds);
    setBulkDeleteProgress({ current: 0, total: idsToDelete.length, currentId: '' });

    const BATCH_SIZE = 5;
    let completed = 0;

    // Process in batches of 5
    for (let i = 0; i < idsToDelete.length; i += BATCH_SIZE) {
      const batch = idsToDelete.slice(i, i + BATCH_SIZE);

      // Run batch in parallel
      await Promise.all(
        batch.map(async (id) => {
          try {
            await sandboxService.delete(id, groupId);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          } catch (error) {
            console.error(`Failed to delete sandbox ${id}:`, error);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          }
        })
      );
    }

    // Invalidate queries to refresh the list
    if (groupId) {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.sandboxes(groupId) });
    } else {
      queryClient.invalidateQueries({ queryKey: ['sandboxes'] });
    }

    setBulkDeleteProgress({
      current: idsToDelete.length,
      total: idsToDelete.length,
      currentId: '',
    });
    setSelectedIds(new Set());

    // Close modal after a brief delay to show completion.
    // Keep isBulkDeleting=true until the modal closes to prevent
    // the confirmation prompt from flashing back during the delay.
    setTimeout(() => {
      setIsBulkDeleting(false);
      setShowBulkDeleteModal(false);
      setBulkDeleteProgress({ current: 0, total: 0, currentId: '' });
    }, 1000);
  };

  const handleDeleteAll = async () => {
    if (total === 0) return;

    setIsBulkDeleting(true);

    // Collect all sandbox IDs to delete
    const allIds: string[] = [];

    if (groupId) {
      // For group-scoped view, use the already-loaded group sandboxes
      allIds.push(...(sandboxes ?? []).map((s) => s.id));
    } else {
      // Fetch all sandbox IDs using the service to ensure proper authentication
      const totalPagesCount = Math.ceil(total / 100); // Fetch in batches of 100
      try {
        for (let pageNum = 1; pageNum <= totalPagesCount; pageNum++) {
          const fetched = await sandboxService.list(pageNum, 100);
          allIds.push(...fetched.map((s) => s.id));
        }
      } catch (error) {
        console.error('Failed to fetch all sandboxes:', error);
        setIsBulkDeleting(false);
        return;
      }
    }

    setBulkDeleteProgress({ current: 0, total: allIds.length, currentId: '' });

    const BATCH_SIZE = 5;
    let completed = 0;

    // Process in batches of 5
    for (let i = 0; i < allIds.length; i += BATCH_SIZE) {
      const batch = allIds.slice(i, i + BATCH_SIZE);

      // Run batch in parallel
      await Promise.all(
        batch.map(async (id) => {
          try {
            await sandboxService.delete(id, groupId);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: allIds.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          } catch (error) {
            console.error(`Failed to delete sandbox ${id}:`, error);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: allIds.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          }
        })
      );
    }

    // Invalidate queries to refresh the list
    if (groupId) {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.sandboxes(groupId) });
    } else {
      queryClient.invalidateQueries({ queryKey: ['sandboxes'] });
    }

    setBulkDeleteProgress({ current: allIds.length, total: allIds.length, currentId: '' });
    setSelectedIds(new Set());

    // Close modal after a brief delay to show completion.
    // Keep isBulkDeleting=true until the modal closes to prevent
    // the confirmation prompt from flashing back during the delay.
    setTimeout(() => {
      setIsBulkDeleting(false);
      setShowDeleteAllModal(false);
      setBulkDeleteProgress({ current: 0, total: 0, currentId: '' });
    }, 1000);
  };

  const isAllSelected =
    filteredSandboxes &&
    filteredSandboxes.length > 0 &&
    filteredSandboxes.every((s) => selectedIds.has(s.id));

  if (isLoading) {
    return <LoadingPage />;
  }

  // Sandboxes view (default)
  return (
    <div className="h-full flex flex-col">
      {/* Header with view toggle */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Sandboxes</h1>
            {groupId && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Group:{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300">{groupId}</span>
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Create Sandbox dropdown */}
          <div className="relative" ref={createSandboxMenuRef}>
            <button
              onClick={() => setShowCreateSandboxMenu(!showCreateSandboxMenu)}
              aria-expanded={showCreateSandboxMenu}
              aria-haspopup="menu"
              className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>
                <span className="hidden sm:inline">Create </span>Sandbox
              </span>
              <ChevronDown className="h-4 w-4" />
            </button>
            {showCreateSandboxMenu && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-56 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-lg z-50"
              >
                <Link
                  to={
                    groupId
                      ? `/sandbox-groups/${encodeURIComponent(groupId)}/sandboxes/new?groupId=${encodeURIComponent(groupId)}`
                      : '/sandboxes/new'
                  }
                  role="menuitem"
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-t-lg"
                  onClick={() => setShowCreateSandboxMenu(false)}
                >
                  <Plus className="h-4 w-4" />
                  <div>
                    <div className="font-medium">Standard Sandbox</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Full configuration options
                    </div>
                  </div>
                </Link>
                <Link
                  to={
                    groupId
                      ? `/sandbox-groups/${encodeURIComponent(groupId)}/sandboxes/new/copilot`
                      : '/sandboxes/new/copilot'
                  }
                  role="menuitem"
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] border-t border-gray-200 dark:border-[#2a2a2a]"
                  onClick={() => setShowCreateSandboxMenu(false)}
                >
                  <Github className="h-4 w-4" />
                  <div>
                    <div className="font-medium">GitHub Copilot Sandbox</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Pre-configured for Copilot
                    </div>
                  </div>
                </Link>
                <Link
                  to={
                    groupId
                      ? `/sandbox-groups/${encodeURIComponent(groupId)}/sandboxes/new/claude`
                      : '/sandboxes/new/claude'
                  }
                  role="menuitem"
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] border-t border-gray-200 dark:border-[#2a2a2a]"
                  onClick={() => setShowCreateSandboxMenu(false)}
                >
                  <Terminal className="h-4 w-4" />
                  <div>
                    <div className="font-medium">Claude Sandbox</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Claude CLI pre-installed
                    </div>
                  </div>
                </Link>
              </div>
            )}
          </div>
          {!groupId && (
            <Link to="/sandboxes/batch">
              <button className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] hover:bg-gray-50 dark:hover:bg-[#2a2a2a] rounded-lg transition-colors">
                <LayoutGrid className="h-4 w-4" />
                <span>
                  <span className="hidden sm:inline">Create </span>Batch
                </span>
              </button>
            </Link>
          )}
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex items-center gap-3 mb-2">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-400" />
          <input
            type="text"
            placeholder="Search by ID or labels"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search sandboxes by ID or labels"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Group by */}
        <div className="relative">
          <button
            onClick={() => setShowGroupByMenu(!showGroupByMenu)}
            aria-expanded={showGroupByMenu}
            aria-haspopup="listbox"
            className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-md transition-colors ${
              groupByGroup
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                : 'border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white hover:bg-gray-50 dark:hover:bg-[#2a2a2a]'
            }`}
          >
            <Layers className="h-4 w-4" />
            <span className="text-gray-600 dark:text-gray-400">Group by:</span>
            <span className="font-medium">{groupByGroup ? 'Sandbox Group' : 'None'}</span>
            <ChevronDown className="h-4 w-4" />
          </button>
          {showGroupByMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowGroupByMenu(false)} />
              <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-lg z-20">
                <button
                  onClick={() => {
                    setGroupByGroup(false);
                    setShowGroupByMenu(false);
                  }}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-[#2a2a2a] first:rounded-t-lg ${
                    !groupByGroup
                      ? 'text-primary-600 dark:text-primary-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  None
                </button>
                <button
                  onClick={() => {
                    setGroupByGroup(true);
                    setShowGroupByMenu(false);
                  }}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-[#2a2a2a] last:rounded-b-lg flex items-center gap-2
                    ${
                      groupByGroup
                        ? 'text-primary-600 dark:text-primary-400 font-medium'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                >
                  <FolderOpen className="h-4 w-4" />
                  Sandbox Group
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="mb-2 flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-lg">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {selectedIds.size} sandbox{selectedIds.size > 1 ? 'es' : ''} selected
          </span>
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          <table className="w-full min-w-max" aria-label="Sandboxes">
            <thead className="bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-[#2a2a2a]">
              <tr>
                <th className="px-4 py-1.5 text-left">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    aria-label="Select all sandboxes"
                    className="rounded-sm border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                  />
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  ID
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Labels
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  State
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Actions
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Snapshot
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Resources
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Created At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#0a0a0a] divide-y divide-gray-200 dark:divide-[#2a2a2a]">
              {!filteredSandboxes || filteredSandboxes.length === 0 ? (
                <tr className="bg-white dark:bg-[#0a0a0a]">
                  <td colSpan={8} className="px-4 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <Box className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                        {searchQuery ? 'No matching sandboxes found.' : 'No Sandboxes yet.'}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                        {searchQuery
                          ? 'Try adjusting your search query.'
                          : 'Spin up a Sandbox to run code in an isolated environment.'}
                      </p>
                      {!searchQuery && (
                        <>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Use the ADC SDK or CLI to create one.
                          </p>
                          <p className="text-sm text-gray-900 dark:text-white">
                            <span className="font-medium">Check out the Onboarding guide</span> to
                            learn more.
                          </p>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : groupByGroup && groupedSandboxes ? (
                // Grouped view
                groupedSandboxes.map(([groupId, sandboxesInGroup]) => (
                  <>
                    {/* Group header row */}
                    <tr
                      key={`group-header-${groupId || 'ungrouped'}`}
                      className="bg-gray-50 dark:bg-[#161616]"
                    >
                      <td colSpan={8} className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <FolderOpen className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          {groupId ? (
                            <Link
                              to={`/sandbox-groups/${encodeURIComponent(groupId)}`}
                              className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400"
                            >
                              {getGroupName(groupId)}
                            </Link>
                          ) : (
                            <span className="font-medium text-gray-500 dark:text-gray-400 italic">
                              {getGroupName(null)}
                            </span>
                          )}
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            ({sandboxesInGroup.length} sandbox
                            {sandboxesInGroup.length !== 1 ? 'es' : ''})
                          </span>
                        </div>
                      </td>
                    </tr>
                    {/* Sandboxes in this group */}
                    {sandboxesInGroup.map((sandbox) => (
                      <tr key={sandbox.id} className="hover:bg-gray-50 dark:hover:bg-[#161616]">
                        <td className="px-4 py-1.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(sandbox.id)}
                            onChange={(e) => handleSelectOne(sandbox.id, e.target.checked)}
                            aria-label={`Select sandbox ${sandbox.id}`}
                            className="rounded-sm border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                          />
                        </td>
                        <td className="px-4 py-1.5 whitespace-nowrap">
                          <Link
                            to={
                              sandbox.sandboxGroupId
                                ? `/sandbox-groups/${encodeURIComponent(sandbox.sandboxGroupId)}/sandboxes/${encodeURIComponent(sandbox.id)}`
                                : `/sandboxes/${encodeURIComponent(sandbox.id)}`
                            }
                            className="text-sm font-medium text-gray-900 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            {sandbox.id}
                          </Link>
                        </td>
                        <td className="px-4 py-1.5 text-sm text-gray-600 dark:text-gray-400">
                          {sandbox.labels && Object.keys(sandbox.labels).length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(sandbox.labels as Record<string, string>).map(
                                ([key, value]) => (
                                  <span
                                    key={key}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                                  >
                                    {key}: {value}
                                  </span>
                                )
                              )}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-4 py-1.5 whitespace-nowrap">
                          <Badge variant={getSandboxStateBadgeVariant(sandbox.state ?? 'Running')}>
                            {sandbox.state || 'Running'}
                          </Badge>
                        </td>
                        <td className="px-4 py-1.5 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            {sandbox.state === 'Stopped' || sandbox.state === 'Idle' ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setResumingIds((prev) => new Set(prev).add(sandbox.id));
                                  resumeMutation.mutate(
                                    {
                                      id: sandbox.id,
                                      sandboxGroupId: sandbox.sandboxGroupId ?? undefined,
                                    },
                                    {
                                      onSettled: () =>
                                        setResumingIds((prev) => {
                                          const next = new Set(prev);
                                          next.delete(sandbox.id);
                                          return next;
                                        }),
                                    }
                                  );
                                }}
                                disabled={resumingIds.has(sandbox.id)}
                                className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50"
                                title="Resume sandbox"
                                aria-label={`Resume sandbox ${sandbox.id}`}
                              >
                                {resumingIds.has(sandbox.id) ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-green-700" />
                                ) : (
                                  <Play className="h-4 w-4 text-green-700 fill-green-700" />
                                )}
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (sandbox.lifecycle?.autoSuspendPolicy?.mode === 'None') {
                                    setStopConfirmTarget(sandbox);
                                  } else {
                                    setStoppingIds((prev) => new Set(prev).add(sandbox.id));
                                    stopMutation.mutate(
                                      {
                                        id: sandbox.id,
                                        sandboxGroupId: sandbox.sandboxGroupId ?? undefined,
                                      },
                                      {
                                        onSettled: () =>
                                          setStoppingIds((prev) => {
                                            const next = new Set(prev);
                                            next.delete(sandbox.id);
                                            return next;
                                          }),
                                      }
                                    );
                                  }
                                }}
                                disabled={stoppingIds.has(sandbox.id)}
                                className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50"
                                title="Stop sandbox"
                                aria-label={`Stop sandbox ${sandbox.id}`}
                              >
                                {stoppingIds.has(sandbox.id) ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                                ) : (
                                  <Square className="h-4 w-4 text-red-600 fill-red-600" />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {sandbox.snapshotId || '-'}
                        </td>
                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {sandbox.resources?.cpu && sandbox.resources?.memory
                            ? `${sandbox.resources.cpu} CPU / ${sandbox.resources.memory}`
                            : '-'}
                        </td>
                        <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {sandbox.createdAt ? new Date(sandbox.createdAt).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </>
                ))
              ) : (
                // Flat view (no grouping)
                filteredSandboxes.map((sandbox) => (
                  <tr key={sandbox.id} className="hover:bg-gray-50 dark:hover:bg-[#161616]">
                    <td className="px-4 py-1.5">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(sandbox.id)}
                        onChange={(e) => handleSelectOne(sandbox.id, e.target.checked)}
                        aria-label={`Select sandbox ${sandbox.id}`}
                        className="rounded-sm border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                      />
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      <Link
                        to={
                          sandbox.sandboxGroupId
                            ? `/sandbox-groups/${encodeURIComponent(sandbox.sandboxGroupId)}/sandboxes/${encodeURIComponent(sandbox.id)}`
                            : `/sandboxes/${encodeURIComponent(sandbox.id)}`
                        }
                        className="text-sm font-medium text-gray-900 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400"
                      >
                        {sandbox.id}
                      </Link>
                    </td>
                    <td className="px-4 py-1.5 text-sm text-gray-600 dark:text-gray-400">
                      {sandbox.labels && Object.keys(sandbox.labels).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(sandbox.labels as Record<string, string>).map(
                            ([key, value]) => (
                              <span
                                key={key}
                                className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                              >
                                {key}: {value}
                              </span>
                            )
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      <Badge variant={getSandboxStateBadgeVariant(sandbox.state ?? 'Running')}>
                        {sandbox.state || 'Running'}
                      </Badge>
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {sandbox.state === 'Stopped' || sandbox.state === 'Idle' ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setResumingIds((prev) => new Set(prev).add(sandbox.id));
                              resumeMutation.mutate(
                                {
                                  id: sandbox.id,
                                  sandboxGroupId: sandbox.sandboxGroupId ?? undefined,
                                },
                                {
                                  onSettled: () =>
                                    setResumingIds((prev) => {
                                      const next = new Set(prev);
                                      next.delete(sandbox.id);
                                      return next;
                                    }),
                                }
                              );
                            }}
                            disabled={resumingIds.has(sandbox.id)}
                            className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50"
                            title="Resume sandbox"
                            aria-label={`Resume sandbox ${sandbox.id}`}
                          >
                            {resumingIds.has(sandbox.id) ? (
                              <Loader2 className="h-4 w-4 animate-spin text-green-700" />
                            ) : (
                              <Play className="h-4 w-4 text-green-700 fill-green-700" />
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (sandbox.lifecycle?.autoSuspendPolicy?.mode === 'None') {
                                setStopConfirmTarget(sandbox);
                              } else {
                                setStoppingIds((prev) => new Set(prev).add(sandbox.id));
                                stopMutation.mutate(
                                  {
                                    id: sandbox.id,
                                    sandboxGroupId: sandbox.sandboxGroupId ?? undefined,
                                  },
                                  {
                                    onSettled: () =>
                                      setStoppingIds((prev) => {
                                        const next = new Set(prev);
                                        next.delete(sandbox.id);
                                        return next;
                                      }),
                                  }
                                );
                              }
                            }}
                            disabled={stoppingIds.has(sandbox.id)}
                            className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50"
                            title="Stop sandbox"
                            aria-label={`Stop sandbox ${sandbox.id}`}
                          >
                            {stoppingIds.has(sandbox.id) ? (
                              <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                            ) : (
                              <Square className="h-4 w-4 text-red-600 fill-red-600" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {sandbox.snapshotId || '-'}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {sandbox.resources?.cpu && sandbox.resources?.memory
                        ? `${sandbox.resources.cpu} CPU / ${sandbox.resources.memory}`
                        : '-'}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {sandbox.createdAt ? new Date(sandbox.createdAt).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <nav
          aria-label="Sandboxes pagination"
          className="flex items-center justify-between px-6 py-2 border-t border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a]"
        >
          <div className="flex items-center gap-4">
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              aria-label="Sandboxes per page"
              className="text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white rounded-md px-3 py-1.5 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {searchQuery && filteredSandboxes && filteredSandboxes.length !== total ? (
                <>
                  Showing {filteredSandboxes.length} of {total} sandboxes (filtered)
                </>
              ) : (
                <>
                  Showing {sandboxes && sandboxes.length > 0 ? (page - 1) * pageSize + 1 : 0} to{' '}
                  {Math.min(page * pageSize, total)} of {total} sandboxes
                </>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleFirstPage}
              disabled={page <= 1}
              aria-label="First page"
              className="px-2 py-1.5 text-xs rounded-sm hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
            >
              First
            </button>
            <button
              onClick={handlePreviousPage}
              disabled={page <= 1}
              aria-label="Previous page"
              className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={handleNextPage}
              disabled={page >= totalPages}
              aria-label="Next page"
              className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={handleLastPage}
              disabled={page >= totalPages}
              aria-label="Last page"
              className="px-2 py-1.5 text-xs rounded-sm hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
            >
              Last
            </button>
            {/* Delete All Button - inline with pagination */}
            {total > 0 && (
              <button
                onClick={() => setShowDeleteAllModal(true)}
                className="ml-4 flex items-center gap-2 px-2 py-1.5 text-xs rounded-sm hover:bg-red-50 dark:hover:bg-red-950 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Delete All ({total})
              </button>
            )}
          </div>
        </nav>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Sandbox"
        message={`Are you sure you want to delete sandbox "${deleteTarget?.id}"? This action cannot be undone and any data in the sandbox will be lost.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Stop Confirmation Modal (for suspend mode = None) */}
      <ConfirmModal
        isOpen={!!stopConfirmTarget}
        onClose={() => setStopConfirmTarget(null)}
        onConfirm={() => {
          if (stopConfirmTarget) {
            setStoppingIds((prev) => new Set(prev).add(stopConfirmTarget.id));
            stopMutation.mutate(
              {
                id: stopConfirmTarget.id,
                sandboxGroupId: stopConfirmTarget.sandboxGroupId ?? undefined,
              },
              {
                onSettled: () =>
                  setStoppingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(stopConfirmTarget.id);
                    return next;
                  }),
              }
            );
            setStopConfirmTarget(null);
          }
        }}
        title="Stop Sandbox"
        message={`This sandbox has suspend mode set to "None". Stopping it will discard all in-VM state (memory, disk changes). When resumed, it will start fresh from the original base image. Are you sure you want to proceed?`}
        confirmText="Stop"
        variant="primary"
        isLoading={stopMutation.isPending}
      />

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Delete ${selectedIds.size} sandboxes`}
            className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl max-w-md w-full mx-4"
          >
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Delete {selectedIds.size} Sandbox{selectedIds.size > 1 ? 'es' : ''}?
              </h2>

              {!isBulkDeleting ? (
                <>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to delete {selectedIds.size} sandbox
                    {selectedIds.size > 1 ? 'es' : ''}? This action cannot be undone and any data in
                    the sandboxes will be lost.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowBulkDeleteModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#161616] border border-gray-300 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Delete All
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>Deleting sandboxes...</span>
                      <span>
                        {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
                      </span>
                    </div>
                    <div
                      className="w-full bg-gray-200 dark:bg-[#161616] rounded-full h-2.5"
                      role="progressbar"
                      aria-valuenow={bulkDeleteProgress.current}
                      aria-valuemin={0}
                      aria-valuemax={bulkDeleteProgress.total}
                      aria-label="Deletion progress"
                    >
                      <div
                        className="bg-red-600 h-2.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                    {bulkDeleteProgress.currentId && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Deleting: {bulkDeleteProgress.currentId}
                      </p>
                    )}
                  </div>
                  {bulkDeleteProgress.current === bulkDeleteProgress.total && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      ✓ All sandboxes deleted successfully
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete All Modal */}
      {showDeleteAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Delete all sandboxes"
            className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl max-w-md w-full mx-4"
          >
            <div className="p-6">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-4">
                ⚠️ Delete All {total} Sandboxes?
              </h2>

              {!isBulkDeleting ? (
                <>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    This will permanently delete <strong>ALL {total} sandboxes</strong> across all
                    pages.
                  </p>
                  <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-6">
                    This action cannot be undone and all data will be lost!
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowDeleteAllModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#161616] border border-gray-300 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteAll}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Delete All {total} Sandboxes
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>Deleting all sandboxes...</span>
                      <span>
                        {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
                      </span>
                    </div>
                    <div
                      className="w-full bg-gray-200 dark:bg-[#161616] rounded-full h-2.5"
                      role="progressbar"
                      aria-valuenow={bulkDeleteProgress.current}
                      aria-valuemin={0}
                      aria-valuemax={bulkDeleteProgress.total}
                      aria-label="Deletion progress"
                    >
                      <div
                        className="bg-red-600 h-2.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                    {bulkDeleteProgress.currentId && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Deleting: {bulkDeleteProgress.currentId}
                      </p>
                    )}
                  </div>
                  {bulkDeleteProgress.current === bulkDeleteProgress.total && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      ✓ All sandboxes deleted successfully
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
